import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, XCircle, Loader2 } from "lucide-react";

import { API_URL } from "@/utils/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) pollPaymentStatus(sessionId);
    else setStatus("failed");
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 10) { setStatus("failed"); return; }
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setPaymentDetails(data);
      if (data.payment_status === "paid") { setStatus("success"); return; }
      else if (data.status === "expired") { setStatus("failed"); return; }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      if (attempts < 9) setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      else setStatus("failed");
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-[#2563eb] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f0f0f] mb-2">Processing Payment</h1>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f0f0f] mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">Thank you for subscribing to FinAI. Your account has been upgraded.</p>
            {paymentDetails && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                <p className="text-xl font-semibold text-[#0f0f0f]">${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}</p>
              </div>
            )}
            <Link to="/dashboard">
              <Button className="h-12 px-8 rounded-full bg-[#0f0f0f] hover:bg-[#1a1a1a] text-white font-medium" data-testid="success-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {status === "failed" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#0f0f0f] mb-2">Payment Failed</h1>
            <p className="text-gray-600 mb-6">Something went wrong with your payment. Please try again.</p>
            <div className="flex flex-col gap-3">
              <Link to="/pricing"><Button className="w-full h-12 rounded-full bg-[#0f0f0f] hover:bg-[#1a1a1a] text-white font-medium" data-testid="failed-retry-btn">Try Again</Button></Link>
              <Link to="/"><Button variant="outline" className="w-full h-12 rounded-full" data-testid="failed-home-btn">Back to Home</Button></Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
